/**
 * DebugControls — only rendered when `?debug` is present in the URL.
 *
 * Provides:
 *  - Leva panel: gem material uniforms (ior, fresnel, bounces, aberration),
 *    ring rotation, and environment background toggle.
 *  - OrbitControls: free camera instead of scroll-driven CameraRig.
 */

import { OrbitControls } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useControls, folder } from 'leva';
import { useEffect } from 'react';
import {
    DEFAULT_GEM_EMISSIVE_STRENGTH,
    getLastGemMaterial,
    setGemDark,
    setGemEmissiveStrength,
} from '../../lib/three/createGemMaterial';
import useStore from '../../store/useStore';

export default function DebugControls() {
    const gemControls = useControls('Gem Material', {
        ior:               { value: 1.33,  min: 1.0, max: 3.0,  step: 0.01 },
        fresnel:           { value: 0.25,  min: 0.0, max: 1.0,  step: 0.01 },
        bounces:           { value: 3,     min: 1,   max: 8,    step: 1 },
        aberration:        { value: 0.01,  min: 0.0, max: 0.1,  step: 0.001 },
        emissiveStrength:  { value: DEFAULT_GEM_EMISSIVE_STRENGTH, min: 0.0, max: 4.0, step: 0.05 },
        // Force the gem's dark-mode glow on for tuning (the scroll engine that
        // normally drives isDark doesn't run under OrbitControls).
        glowPreview:       { value: false },
        color: folder({
            gemR:          { value: 2.0,   min: 0.0, max: 5.0,  step: 0.05 },
            gemG:          { value: 2.0,   min: 0.0, max: 5.0,  step: 0.05 },
            gemB:          { value: 2.0,   min: 0.0, max: 5.0,  step: 0.05 },
        }),
    });

    // Push Leva values into the gem material uniforms every frame.
    // This is intentionally per-frame (cheap uniform set) rather than a useEffect
    // so changes are immediate without needing a re-render cycle.
    useFrame(() => {
        const mat = getLastGemMaterial();
        if (!mat) return;
        if (mat.uniforms.ior)                mat.uniforms.ior.value                = gemControls.ior;
        if (mat.uniforms.fresnel)            mat.uniforms.fresnel.value            = gemControls.fresnel;
        if (mat.uniforms.bounces)            mat.uniforms.bounces.value            = gemControls.bounces;
        if (mat.uniforms.aberrationStrength) mat.uniforms.aberrationStrength.value = gemControls.aberration;
        setGemEmissiveStrength(mat, gemControls.emissiveStrength);
        // Force the dark-mode glow on while previewing; otherwise mirror the store,
        // so toggling preview off actually turns the glow back off under OrbitControls.
        setGemDark(mat, gemControls.glowPreview || useStore.getState().isDark);
        if (mat.uniforms.color)              mat.uniforms.color.value.setRGB(gemControls.gemR, gemControls.gemG, gemControls.gemB);
        mat.uniformsNeedUpdate = true;
    });

    // Log all uniform keys once so it's easy to see what's available
    useEffect(() => {
        const mat = getLastGemMaterial();
        if (mat) {
            console.debug('[DebugControls] gem material uniforms:', Object.keys(mat.uniforms));
        }
    }, []);

    return <OrbitControls makeDefault />;
}
